import { useState, useEffect, useRef } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { loadMusicFromDocProfile } from "../utils/musicExport";
import { createCookie, readCookie } from "../utils/helper";
import { registerErrorPopupHandler, reportApiError } from "../utils/errorPopupBridge";
import type { GenerateMusicResult } from "../utils/generateMusic";
import MusicPlayerScreen from "../components/MusicPlayerScreen";
import ErrorPopup from "../components/ErrorPopup";

type MusicPlayerLocationState = {
  initialMusicResult?: GenerateMusicResult;
  initialPrompt?: string;
  initialSelections?: Record<string, string[]>;
};

export default function MusicPlayerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as MusicPlayerLocationState | null;
  const [searchParams] = useSearchParams();
  const userId =
    searchParams.get("uId") ||
    readCookie("userguid") ||
    "";
  const docId =
    searchParams.get("docId") ||
    "";

  const [musicResult, setMusicResult] = useState<GenerateMusicResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const loadedKeyRef = useRef<string | null>(null);
  const isDark = localStorage.getItem("theme") !== "light";

  useEffect(() => {
    registerErrorPopupHandler((message) => {
      setErrorMessage(message);
      setShowErrorPopup(true);
    });
    return () => registerErrorPopupHandler(null);
  }, []);

  useEffect(() => {
    if (!locationState?.initialMusicResult) return;

    setMusicResult(locationState.initialMusicResult);
    setIsLoading(false);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, locationState, navigate]);

  useEffect(() => {
    if (locationState?.initialMusicResult) return;

    if (!docId) {
      reportApiError(new Error(t("generic_error.we_could_not_complete") || "We couldn't find your song. Please check your link and try again."));
      setIsLoading(false);
      return;
    }

    if (userId) {
      createCookie("userguid", userId, 1);
    }

    const loadKey = `${docId}:${userId}`;
    if (loadedKeyRef.current === loadKey) {
      return;
    }
    loadedKeyRef.current = loadKey;

    async function fetchSong() {
      try {
        setIsLoading(true);
        const result = await loadMusicFromDocProfile(docId);
        setMusicResult(result);
      } catch (err) {
        console.error("Failed to load song from doc profile:", err);
        reportApiError(err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchSong();
  }, [docId, userId, locationState?.initialMusicResult, t]);

  return (
    <>
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#687585]/80">
          <div className="flex flex-col items-center gap-6">
            <div className="flex sm:items-end items-center sm:justify-normal justify-center gap-3">
              <span className="size-4.5 rounded-full bg-white animate-bounce [animation-delay:0ms]" />
              <span className="size-5.5 rounded-full bg-white animate-bounce [animation-delay:150ms]" />
              <span className="size-6.25 rounded-full bg-white animate-bounce [animation-delay:300ms]" />
            </div>
            <p className="text-[20px] font-bold text-[#fdfdfd] tracking-[0.4px] leading-[1.2] text-center">
              {t("app.loadingSong") || "Loading your song..."}
            </p>
          </div>
        </div>
      )}

      {showErrorPopup && (
        <ErrorPopup
          message={errorMessage}
          onClose={() => {
            setShowErrorPopup(false);
            setErrorMessage(null);
          }}
        />
      )}

      <MusicPlayerScreen
        musicResult={musicResult}
        isDark={isDark}
      />
    </>
  );
}
